import React, { useState } from 'react';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import TravelMapShell from './TravelMapShell.jsx';
import { trackLabelsToggle } from '../utils/analytics';
import { PALETTE } from '../theme';
import { NATURE_DEFAULT_COLOR } from './mapDefaultColors';
import { LABEL_OUTLINE_COLOR, LABEL_OUTLINE_WIDTH, LABEL_OUTLINE_WIDTH_HOVER } from './mapGeographyStyles';

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

// Caribbean island nations and territories with island-centroid coordinates.
// `mapLabel` (optional) is a shortened name used for the on-map text only —
// the checklist always shows the full `name`.
const CARIBBEAN_ISLANDS = [
    { id: 'anguilla', name: 'Anguilla', region: 'United Kingdom', coords: [-63.07, 18.22] },
    { id: 'antigua-barbuda', name: 'Antigua and Barbuda', region: 'Sovereign nation', coords: [-61.79, 17.06], mapLabel: 'Antigua & Barbuda' },
    { id: 'aruba', name: 'Aruba', region: 'Netherlands', coords: [-69.97, 12.52] },
    { id: 'bahamas', name: 'Bahamas', region: 'Sovereign nation', coords: [-77.40, 24.80] },
    { id: 'barbados', name: 'Barbados', region: 'Sovereign nation', coords: [-59.54, 13.18] },
    { id: 'bonaire', name: 'Bonaire', region: 'Netherlands', coords: [-68.27, 12.18] },
    { id: 'british-virgin', name: 'British Virgin Islands', region: 'United Kingdom', coords: [-64.62, 18.45], mapLabel: 'British Virgin Is.' },
    { id: 'cayman', name: 'Cayman Islands', region: 'United Kingdom', coords: [-81.25, 19.32] },
    { id: 'cuba', name: 'Cuba', region: 'Sovereign nation', coords: [-79.00, 21.90] },
    { id: 'curacao', name: 'Curaçao', region: 'Netherlands', coords: [-68.99, 12.18] },
    { id: 'dominica', name: 'Dominica', region: 'Sovereign nation', coords: [-61.37, 15.41] },
    { id: 'dominican-republic', name: 'Dominican Republic', region: 'Sovereign nation', coords: [-70.50, 18.85], mapLabel: 'Dominican Rep.' },
    { id: 'grenada', name: 'Grenada', region: 'Sovereign nation', coords: [-61.68, 12.12] },
    { id: 'guadeloupe', name: 'Guadeloupe', region: 'France', coords: [-61.55, 16.25] },
    { id: 'haiti', name: 'Haiti', region: 'Sovereign nation', coords: [-72.60, 19.00] },
    { id: 'jamaica', name: 'Jamaica', region: 'Sovereign nation', coords: [-77.30, 18.10] },
    { id: 'martinique', name: 'Martinique', region: 'France', coords: [-61.02, 14.64] },
    { id: 'montserrat', name: 'Montserrat', region: 'United Kingdom', coords: [-62.19, 16.74] },
    { id: 'puerto-rico', name: 'Puerto Rico', region: 'United States', coords: [-66.50, 18.22] },
    { id: 'st-kitts-nevis', name: 'Saint Kitts and Nevis', region: 'Sovereign nation', coords: [-62.70, 17.28], mapLabel: 'St. Kitts & Nevis' },
    { id: 'st-lucia', name: 'Saint Lucia', region: 'Sovereign nation', coords: [-60.98, 13.90] },
    { id: 'st-vincent', name: 'Saint Vincent and the Grenadines', region: 'Sovereign nation', coords: [-61.20, 13.05], mapLabel: 'St. Vincent & Gren.' },
    { id: 'sint-maarten', name: 'Sint Maarten', region: 'Netherlands', coords: [-63.05, 18.04] },
    { id: 'trinidad-tobago', name: 'Trinidad and Tobago', region: 'Sovereign nation', coords: [-61.25, 10.45], mapLabel: 'Trinidad & Tobago' },
    { id: 'turks-caicos', name: 'Turks and Caicos Islands', region: 'United Kingdom', coords: [-71.80, 21.75], mapLabel: 'Turks & Caicos' },
    { id: 'us-virgin', name: 'U.S. Virgin Islands', region: 'United States', coords: [-64.83, 17.75], mapLabel: 'U.S. Virgin Is.' },
].sort((a, b) => a.name.localeCompare(b.name));

// Per-island label placement {x, y, anchor} relative to the dot. The Lesser
// Antilles form a tight chain and the ABC islands cluster together, so labels
// are fanned out by hand to avoid collisions. Islands not listed here use the
// default (centered above).
const DEFAULT_LABEL_OFFSET = { x: 0, y: -9, anchor: 'middle' };
const LABEL_OFFSETS = {
    cayman: { x: -9, y: 3, anchor: 'end' },
    haiti: { x: -9, y: 0, anchor: 'end' },
    'dominican-republic': { x: 0, y: -10, anchor: 'middle' },
    'puerto-rico': { x: -9, y: 3, anchor: 'end' },
    'british-virgin': { x: 0, y: -11, anchor: 'middle' },
    'us-virgin': { x: -34, y: 3, anchor: 'middle' },
    anguilla: { x: 9, y: -6, anchor: 'start' },
    'sint-maarten': { x: 9, y: 3, anchor: 'start' },
    'st-kitts-nevis': { x: -7, y: 2, anchor: 'end' },
    'antigua-barbuda': { x: 9, y: 2, anchor: 'start' },
    montserrat: { x: -7, y: 3, anchor: 'end' },
    guadeloupe: { x: 9, y: 3, anchor: 'start' },
    dominica: { x: 9, y: 3, anchor: 'start' },
    martinique: { x: 9, y: 3, anchor: 'start' },
    'st-lucia': { x: 9, y: 3, anchor: 'start' },
    barbados: { x: 8, y: 2, anchor: 'start' },
    'st-vincent': { x: -9, y: 4, anchor: 'end' },
    grenada: { x: -9, y: 4, anchor: 'end' },
    'trinidad-tobago': { x: 9, y: 4, anchor: 'start' },
    aruba: { x: -4, y: -7, anchor: 'end' },
    curacao: { x: -8, y: 12, anchor: 'middle' },
    bonaire: { x: 6, y: -5, anchor: 'start' },
};

const CaribbeanMapSection = () => {
    const [showLabels, setShowLabels] = useState(true);
    const [hoveredIsland, setHoveredIsland] = useState(null);

    const labelsToggle = (
        <FormControlLabel
            control={
                <Switch
                    checked={showLabels}
                    onChange={(e) => { setShowLabels(e.target.checked); trackLabelsToggle('caribbean', e.target.checked); }}
                    size="small"
                />
            }
            label={<Typography variant="body2" sx={{ fontSize: '13px' }}>Labels</Typography>}
        />
    );

    const renderMap = ({ visited, hexColor, toggle }) => (
        <ComposableMap
            projection="geoMercator"
            projectionConfig={{
                scale: 1500,
                center: [-72, 18.5]
            }}
            width={800}
            height={600}
            style={{ width: '100%', height: 'auto' }}
        >
            <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                    geographies.map((geo) => (
                        <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            style={{
                                default: { fill: '#eef2f4', stroke: '#cbd5db', strokeWidth: 2.5, outline: 'none' },
                                hover: { fill: '#eef2f4', stroke: '#cbd5db', strokeWidth: 2.5, outline: 'none' },
                                pressed: { fill: '#eef2f4', stroke: '#cbd5db', strokeWidth: 2.5, outline: 'none' }
                            }}
                        />
                    ))
                }
            </Geographies>

            {CARIBBEAN_ISLANDS.map((island) => {
                const isVisited = visited.includes(island.id);
                const isHovered = hoveredIsland === island.id;
                const offset = LABEL_OFFSETS[island.id] || DEFAULT_LABEL_OFFSET;

                return (
                    <Marker
                        key={island.id}
                        coordinates={island.coords}
                        onClick={() => toggle(island.id)}
                        onMouseEnter={() => setHoveredIsland(island.id)}
                        onMouseLeave={() => setHoveredIsland(null)}
                        style={{ cursor: 'pointer' }}
                    >
                        <circle
                            r={isHovered ? 8 : 6}
                            fill={isVisited ? hexColor : PALETTE.textVeryFaint}
                            stroke={isVisited ? '#ffffff' : '#95a5a6'}
                            strokeWidth={isHovered ? 2.5 : 1.5}
                            style={{ transition: 'all 0.2s ease' }}
                        />
                        {(isHovered || (showLabels && isVisited)) && (
                            <text
                                textAnchor={isHovered ? 'middle' : offset.anchor}
                                x={isHovered ? 0 : offset.x}
                                y={isHovered ? -12 : offset.y}
                                style={{
                                    fontFamily: 'system-ui',
                                    fontSize: isHovered ? 11 : 7,
                                    fontWeight: 'bold',
                                    fill: isHovered ? PALETTE.text : hexColor,
                                    pointerEvents: 'none',
                                    paintOrder: 'stroke',
                                    stroke: LABEL_OUTLINE_COLOR,
                                    strokeWidth: isHovered ? LABEL_OUTLINE_WIDTH_HOVER : LABEL_OUTLINE_WIDTH,
                                    strokeLinejoin: 'round'
                                }}
                            >
                                {isHovered ? island.name : (island.mapLabel || island.name)}
                            </text>
                        )}
                    </Marker>
                );
            })}
        </ComposableMap>
    );

    return (
        <TravelMapShell
            mapType="caribbean"
            analyticsLocationType="caribbean_island"
            trackingLabel="Caribbean Map"
            storageKey="caribbean_map"
            title="🏝️ Caribbean Travel Map"
            subtitle="Click on Caribbean islands you've visited, or use the checklist below!"
            items={CARIBBEAN_ISLANDS.map(item => ({ id: item.id, name: item.name, subtitle: item.region }))}
            noun="Islands"
            unitLabel="islands"
            captionUnit="Caribbean islands"
            defaultColor={NATURE_DEFAULT_COLOR}
            share={{
                region: 'Caribbean',
                fileName: 'visited-caribbean-map.png',
                trackId: 'caribbean'
            }}
            extraOptions={labelsToggle}
            showLegend
            renderMap={renderMap}
        />
    );
};

export default CaribbeanMapSection;
